import * as Sentry from '@sentry/react';
import { TeamMember } from '@superfunnel/types';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import Switch from '@superfunnel/ui-core/v2/Switch';
import { emailIsValid, formFieldEmailValidator } from '@superfunnel/utils';
import { diff } from 'deep-diff';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuditLog } from '../contexts/AuditLogContext';
import { UserContext } from '../contexts/UserContext';
import { addMember, updateMember } from '../services/TeamMembersService';
import { distinct } from '../utils';

interface Props {
  member?: TeamMember;
  open: boolean;
  onClose: () => void;
}

export default function AddMemberModal({ member, open, onClose }: Props) {
  const { selectedAccount } = useContext(UserContext);
  const auditLog = useAuditLog();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamMember['role']>();
  const [features, setFeatures] = useState<string[]>([]);
  const [emailTouched, setEmailTouched] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!open) return;

    setName(member?.name ?? '');
    setEmail(member?.email ?? '');
    setEmailTouched(false);
    setRole(member?.role ?? 'member');
    setFeatures(member?.features ?? []);
    setSaving(false);
    setError(undefined);
  }, [open, member]);

  const addAccountMember = useCallback(async () => {
    if (saving) return;
    if (!selectedAccount) return;

    if (!name || name.length < 3) {
      setError(new Error('Name must be at least 3 characters'));
      return;
    }

    if (!email || !emailIsValid(email)) {
      setError(new Error('Invalid email'));
      return;
    }

    setSaving(true);

    try {
      if (!member) {
        await addMember(
          selectedAccount?.id ?? '',
          email.toLowerCase(),
          name,
          role ?? 'member',
          features,
        );
        auditLog.log('create', 'Invite member', 'team', undefined, undefined);
      } else {
        await updateMember(
          selectedAccount?.id ?? '',
          member.userId,
          name,
          role ?? member.role,
          features,
        );
        auditLog.log(
          'update',
          'Update team member',
          'team',
          member.userId,
          undefined,
          diff(
            { role: member.role, features: member.features },
            { role, features },
          ),
        );
      }
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [
    auditLog,
    email,
    features,
    member,
    name,
    onClose,
    role,
    saving,
    selectedAccount,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: member ? 'Save' : 'Add',
        type: 'primary',
        loading: saving,
        onClick: () => addAccountMember(),
      },
    ],
    [saving, member, onClose, addAccountMember],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      title={member ? 'Edit Member' : 'Add a New Member'}
    >
      <div className="flex min-h-48 flex-col gap-4">
        <Input
          title="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          title="Email"
          value={email}
          onChange={(e) => {
            setEmailTouched(true);
            setEmail(e.target.value);
          }}
          disabled={!!member}
          validation={
            emailTouched ? formFieldEmailValidator(email, true) : undefined
          }
        />
        <div className="flex flex-col justify-start gap-1">
          <FieldTitle title="Role" />
          <Select
            value={role}
            onValueChange={(e: TeamMember['role']) => setRole(e)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-1 flex items-center space-x-3">
          <Switch
            checked={features.includes('viewFinancialData') || role === 'admin'}
            disabled={role === 'admin'}
            onCheckedChange={(checked) => {
              if (checked) {
                setFeatures(distinct([...features, 'viewFinancialData']));
              } else {
                setFeatures(features.filter((f) => f !== 'viewFinancialData'));
              }
            }}
          />
          <Label className="text-sm font-medium">View Financial Data</Label>
        </div>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'An error occurred'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
